import { Node } from "./types";

export type NodeCommentTargetType = "company" | "person" | "address";

export interface NodeCommentTarget {
  nodeType: NodeCommentTargetType;
  nodeKey: string;
  typeLabel: "Company" | "Person" | "Address";
  displayLabel: string;
}

const normalizeValue = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const formatNodeAddress = (properties: Record<string, unknown> | undefined): string => {
  if (!properties) {
    return "";
  }

  return [
    normalizeValue(properties.street),
    normalizeValue(properties.postal_address),
    normalizeValue(properties.postal_code),
  ]
    .filter((value) => value.length > 0)
    .join(", ");
};

export const getNodeCommentTarget = (node: Node): NodeCommentTarget | null => {
  if (node.type === "Company") {
    const organizationNumber = normalizeValue(node.properties?.organization_number);
    if (!organizationNumber) {
      return null;
    }

    const organizationName = normalizeValue(node.properties?.organization_name);
    return {
      nodeType: "company",
      nodeKey: organizationNumber,
      typeLabel: "Company",
      displayLabel: organizationName || organizationNumber,
    };
  }

  if (node.type === "FunktionarPerson") {
    const personalNumber = normalizeValue(node.properties?.funktionar_personal_number);
    if (!personalNumber) {
      return null;
    }

    const personName = normalizeValue(node.properties?.funktionar_namn);
    return {
      nodeType: "person",
      nodeKey: personalNumber,
      typeLabel: "Person",
      displayLabel: personName ? `${personName} (${personalNumber})` : personalNumber,
    };
  }

  if (node.type === "Address") {
    const address = formatNodeAddress(node.properties);
    if (!address) {
      return null;
    }

    return {
      nodeType: "address",
      nodeKey: address,
      typeLabel: "Address",
      displayLabel: address,
    };
  }

  return null;
};
